import type { Notification } from '@testflow/shared';
import type { TFunction } from 'i18next';
import { pickLang } from './pick-lang.js';

// #202: Textbau der Glocke als reine Funktionen (ohne DOM testbar). Zeile 1 nennt Art + ID,
// Zeile 2 den Titel. Fehlt ein Objekt (gelöscht, projektfremd, alter Eintrag ohne `context`), bleibt
// Zeile 1 der bisherige allgemeine Satz und Zeile 2 entfällt (G3) — nie „undefined", nie leere Zeile 1.

export interface NotificationLines {
  headline: string;
  detail: string | null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null;
}

// #195/#194: Die Weitergabe-Texte tragen Name und Zahl aus dem Payload (`actor_name`, `count`; Plural
// über `_one`/`_other`); `fix_rejected` die Defect-ID (`human_id`). Der Payload ist
// `Record<string, unknown>` — ein Wert falschen Typs wird weggelassen statt eingesetzt, sonst stünde
// dort „42" als Name. Ältere Arten haben keinen dieser Schlüssel und bekommen keine Parameter.
export function notificationText(t: TFunction, n: Notification): string {
  const key = `notif.kind.${n.kind}`;
  const { actor_name: name, count, human_id: id } = n.payload;
  const params: { name?: string; count?: number; id?: string } = {};
  if (typeof name === 'string' && name.trim() !== '') params.name = name;
  if (typeof count === 'number' && Number.isFinite(count)) params.count = count;
  if (typeof id === 'string' && id.trim() !== '') params.id = id;
  return Object.keys(params).length > 0 ? t(key, params) : t(key);
}

// #202: Defect-Kennung für Zeile 1. Ist der Kontext vom Server da, gilt er (null = gelöscht oder
// projektfremd → Rückfall auf den bisherigen Satz). Nur ohne `context` (alter Client/Eintrag) hilft
// die ID aus dem Payload.
function defectIdFor(n: Notification): string | null {
  if (n.context) return n.context.defect?.human_id ?? null;
  return str(n.payload.human_id);
}

export function notificationLines(
  t: TFunction,
  lang: string | null | undefined,
  n: Notification,
): NotificationLines {
  const fallback: NotificationLines = { headline: notificationText(t, n), detail: null };
  const ctx = n.context;

  switch (n.kind) {
    case 'defect_assigned': {
      const id = defectIdFor(n);
      if (!id) return fallback;
      return {
        headline: t('notif.line.assigned', { id }),
        detail: str(ctx?.defect?.title),
      };
    }
    case 'retest_due': {
      if (n.payload.fix_check === true) {
        // #194: Behebung bestätigen — es geht um den Defect, nicht um den Testfall.
        const id = defectIdFor(n);
        if (!id) return fallback;
        return {
          headline: t('notif.line.fixCheck', { id }),
          detail: str(ctx?.defect?.title),
        };
      }
      const tc = ctx?.test_case;
      if (!tc) return fallback;
      // #202: Mit `context` gilt allein der Server (null = gelöscht/projektfremd → kein „wegen"); die
      // Payload-ID zählt nur ohne `context`, wie bei `defect_assigned` (defectIdFor).
      const defectId = defectIdFor(n);
      const title = pickLang(lang, tc.title_de, tc.title_en);
      let detail: string | null;
      if (defectId) {
        detail = title
          ? t('notif.line.because', { title, id: defectId })
          : t('notif.line.becauseOnly', { id: defectId });
      } else {
        detail = title || null;
      }
      return { headline: t('notif.line.retest', { id: tc.human_id }), detail };
    }
    case 'fix_rejected': {
      const id = defectIdFor(n);
      if (!id) return fallback;
      return {
        headline: t('notif.line.notFixed', { id }),
        detail: str(n.payload.comment)?.trim() ?? str(ctx?.defect?.title),
      };
    }
    case 'handover_received':
    case 'handover_taken': {
      const name = str(n.payload.actor_name);
      const ids = n.payload.test_case_ids;
      const tc = ctx?.test_case;
      if (name && tc && Array.isArray(ids) && ids.length === 1) {
        const key = n.kind === 'handover_received' ? 'handoverReceived' : 'handoverTaken';
        return {
          headline: t(`notif.line.${key}`, { name, id: tc.human_id }),
          detail: pickLang(lang, tc.title_de, tc.title_en) || null,
        };
      }
      // Mehrere Tests oder kein Kontext: Anzahl-Satz wie bisher, Zeile 2 = Planname.
      return { headline: fallback.headline, detail: str(n.payload.plan_name) };
    }
    default:
      return fallback;
  }
}

function pad(v: number): string {
  return String(v).padStart(2, '0');
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

// G5: relativ unter 24 h, danach „gestern" (Vortag im Kalender), sonst Datum TT.MM. (en: DD/MM).
// Die Wörter stehen in den Locale-Dateien (`notif.time.*`); nur das Datumsformat hängt an `lang`.
// Ungültiges Datum → leerer Text statt „NaN".
export function formatNotificationTime(
  date: Date,
  now: Date,
  lang: 'de' | 'en',
  t: TFunction,
): string {
  if (Number.isNaN(date.getTime())) return '';
  const diff = now.getTime() - date.getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return t('notif.time.justNow');
  if (min < 60) return t('notif.time.minutesAgo', { count: min });
  const hours = Math.floor(diff / 3_600_000);
  if (hours < 24) return t('notif.time.hoursAgo', { count: hours });
  // Kalendertage vergleichen (nicht 24-h-Blöcke): „vor 25 h" über Mitternacht ist gestern, nicht vorgestern.
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);
  if (dayDiff === 1) return t('notif.time.yesterday');
  const dd = pad(date.getDate());
  const mm = pad(date.getMonth() + 1);
  return lang === 'en' ? `${dd}/${mm}` : `${dd}.${mm}.`;
}
